import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaMinus,
  FaPlus,
  FaTimes,
  FaSearch,
  FaArrowLeft,
} from "react-icons/fa";
import {
  getPublicMenus,
  getRestaurantProfile,
  getMyRestaurantId,
} from "../../utils/api";

const BASE_URL = process.env.REACT_APP_API_BASE_URL_IMG;

function getFullImageUrl(imageUrl) {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("http")) return imageUrl;
  const cleanUrl = imageUrl.replace(/^\/?uploads\//, "");
  return `${BASE_URL}/uploads/${cleanUrl}`;
}

export default function CreateOrderScreen({
  show,
  onOpen,
  onClose,
  restaurantId: propRestaurantId,
  title = "Create Order",
  onSubmit,
  submitText = "Place Order",
  initialCart = [],
  initialType = "parcel",
  initialValue = "",
}) {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cart, setCart] = useState(initialCart);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [orderType, setOrderType] = useState(initialType);
  const [orderInput, setOrderInput] = useState(initialValue);
  const [searchTerm, setSearchTerm] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [currentCaptainId, setCurrentCaptainId] = useState(null);
  const [restaurantId, setRestaurantId] = useState(propRestaurantId || null);

  // NEW: view-cart modal state
  const [showCartModal, setShowCartModal] = useState(false);

  const isUpdate = initialCart.length > 0;

  useEffect(() => {
    if (show) {
      const fetchRestaurantData = async () => {
        try {
          let targetRestaurantId = propRestaurantId;

          if (!targetRestaurantId) {
            try {
              const myRestaurantData = await getMyRestaurantId();
              targetRestaurantId = myRestaurantData.restaurantId;
              setRestaurantId(myRestaurantData.restaurantId);
            } catch (err) {
              console.warn("Failed to get user's restaurant ID:", err);
              setError("Could not determine restaurant");
              setLoading(false);
              return;
            }
          }

          if (targetRestaurantId) {
            try {
              const profile = await getRestaurantProfile(targetRestaurantId);
              setCurrentCaptainId(profile?.currentCaptainId || null);
            } catch (err) {
              console.warn("Failed to load current captain:", err);
            }

            await fetchMenus(targetRestaurantId);
          }
        } catch (err) {
          setError("Failed to load restaurant data");
          setLoading(false);
        }
      };

      fetchRestaurantData();
      setCart(initialCart);
      setOrderType(initialType);
      setOrderInput(initialValue);
      setDiscountPercent("");
    }
  }, [show, propRestaurantId]);

  // In CreateOrderScreen component, add this useEffect to handle internal scrolling
  useEffect(() => {
    if (!show) return;

    const contentElement = document.querySelector(".flex-1.overflow-y-auto");
    if (!contentElement) return;

    let lastScrollTop = 0;

    const handleScroll = (e) => {
      const scrollTop = e.target.scrollTop;

      // Create a custom event to communicate scroll direction to parent
      const scrollEvent = new CustomEvent("childScroll", {
        detail: {
          scrollTop,
          scrollDirection: scrollTop > lastScrollTop ? "down" : "up",
        },
      });

      window.dispatchEvent(scrollEvent);
      lastScrollTop = scrollTop;
    };

    contentElement.addEventListener("scroll", handleScroll);

    return () => {
      contentElement.removeEventListener("scroll", handleScroll);
    };
  }, [show]);

  useEffect(() => {
    if (show && onOpen) {
      onOpen();
    }
  }, [show, onOpen]);
  async function fetchMenus(targetRestaurantId) {
    setLoading(true);
    setError("");
    try {
      const data = await getPublicMenus(targetRestaurantId);
      setMenus(data);
      if (data.length > 0) setSelectedMenuId(data[0].id);
    } catch (err) {
      setError("Failed to load menu");
      console.error("Menu loading error:", err);
    }
    setLoading(false);
  }

  function addToCart(item) {
    setCart((prev) => {
      const found = prev.find((i) => i.id === item.id);
      return found
        ? prev.map((i) => (i.id === item.id ? { ...i, qty: i.qty + 1 } : i))
        : [...prev, { ...item, qty: 1 }];
    });
  }

  function incrementQty(itemId) {
    setCart((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, qty: i.qty + 1 } : i))
    );
  }

  function decrementQty(itemId) {
    setCart((prev) => {
      const found = prev.find((i) => i.id === itemId);
      if (!found) return prev;
      if (found.qty === 1) return prev.filter((i) => i.id !== itemId);
      return prev.map((i) => (i.id === itemId ? { ...i, qty: i.qty - 1 } : i));
    });
  }

  function handlePlaceOrder() {
    if (!isUpdate) {
      if (orderType === "table" && !orderInput.trim()) {
        alert("Please enter a table number");
        return;
      }
      if (orderType === "room" && !orderInput.trim()) {
        alert("Please enter a room number");
        return;
      }
      if (cart.length === 0) {
        alert("Cart is empty");
        return;
      }
      if (!restaurantId) {
        alert("Restaurant not selected");
        return;
      }

      onSubmit(
        cart,
        orderType,
        orderInput.trim(),
        Number(discountPercent || 0),
        currentCaptainId,
        restaurantId
      );
    } else {
      onSubmit(cart);
    }
  }

  if (!show) return null;

  const selectedMenu = menus.find((m) => m.id === selectedMenuId);
  let filteredItems = [];

  if (!searchTerm) {
    filteredItems = (selectedMenu?.items || [])
      .filter((i) => i.active !== false)
      .sort((a, b) => a.name.localeCompare(b.name));
  } else {
    filteredItems = menus
      .flatMap((m) => (m.items || []).filter((i) => i.active !== false))
      .filter((i) => i.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  const totalItems = cart.reduce((s, it) => s + it.qty, 0);
  const subtotal =
    Math.round(
      cart.reduce((s, it) => s + Number(it.price || 0) * it.qty, 0) * 100
    ) / 100;
  const discount =
    Math.round(subtotal * (Number(discountPercent || 0) / 100) * 100) / 100;
  const adjustedSubtotal = Math.round((subtotal - discount) * 100) / 100;
  const tax = Math.round(adjustedSubtotal * 0.05 * 100) / 100;
  const grandTotal = Math.round((adjustedSubtotal + tax) * 100) / 100;

  return (
    <motion.div
      className="absolute inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      transition={{ type: "tween", ease: "easeInOut" }}
    >
      {/* panel: fixed max width, full height inside, overflow-hidden to contain internal scroll */}
      <div className="w-full max-w-md h-full max-h-[calc(100vh-2rem)] bg-white rounded-lg shadow-lg overflow-hidden flex flex-col">
        {/* Fixed Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white sticky top-0 z-30">
          <button onClick={onClose} className="p-2 text-gray-600 hover:text-gray-800">
            <FaArrowLeft size={20} />
          </button>
          <h3 className="text-lg font-bold text-gray-800 flex-1 text-center">{title}</h3>
          <div className="w-10" /> {/* spacer */}
        </div>

        {/* Scrollable Content Area (everything between header and the view-cart button) */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Controls (not sticky) */}
          <div className="p-4 border-b bg-gray-50 space-y-3">
            <div className="flex gap-2">
              {["table", "room", "parcel"].map((type) => (
                <button
                  key={type}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold ${
                    orderType === type ? "bg-rose-500 text-white" : "bg-white text-gray-700 border border-gray-300"
                  }`}
                  onClick={() => {
                    setOrderType(type);
                    setOrderInput("");
                  }}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              {(orderType === "table" || orderType === "room") && (
                <input
                  type="text"
                  placeholder={orderType === "table" ? "Table Number" : "Room Number"}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={orderInput}
                  onChange={(e) => setOrderInput(e.target.value)}
                />
              )}
              <input
                type="number"
                placeholder="Discount %"
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={discountPercent}
                onChange={(e) => {
                  let v = e.target.value.replace(/\D/g, "");
                  if (v === "") {
                    setDiscountPercent("");
                    return;
                  }
                  const n = Math.min(100, Math.max(0, parseInt(v, 10) || 0));
                  setDiscountPercent(String(n));
                }}
                min="0"
                max="100"
              />
            </div>

            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Search items..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Categories (kept visible, horizontally scrollable) */}
          {menus.length > 0 && (
            <div className="px-4 py-3 border-b bg-white">
              <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
                {menus.map((menu) => (
                  <button
                    key={menu.id}
                    onClick={() => {
                      setSelectedMenuId(menu.id);
                      setSearchTerm("");
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                      selectedMenuId === menu.id ? "bg-rose-500 text-white" : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {menu.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Items: main scrollable area. Keep selector class used by scroll listener */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-rose-500" />
              </div>
            ) : error ? (
              <div className="text-red-500 text-center py-6">{error}</div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                {menus.length === 0 ? "No menu categories available" : `No items found${searchTerm ? ` for "${searchTerm}"` : " in this category"}`}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {filteredItems.map((item) => {
                  const cartItem = cart.find((c) => c.id === item.id);
                  return (
                    <div key={item.id} className="bg-white rounded-lg p-3 border border-gray-200">
                      {item.imageUrl && (
                        <img
                          src={getFullImageUrl(item.imageUrl)}
                          alt={item.name}
                          className="w-full h-24 object-cover rounded mb-2"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      )}
                      <div className="font-medium text-sm text-center mb-1 line-clamp-2">{item.name}</div>
                      <div className="text-rose-600 font-bold text-center mb-3">₹{item.price || 0}</div>

                      {cartItem ? (
                        <div className="flex items-center justify-between">
                          <button onClick={() => decrementQty(item.id)} className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
                            <FaMinus size={12} />
                          </button>
                          <span className="font-semibold">{cartItem.qty}</span>
                          <button onClick={() => incrementQty(item.id)} className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
                            <FaPlus size={12} />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => addToCart(item)} className="w-full py-2 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg text-sm font-semibold">
                          Add
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* REPLACED: Fixed Cart Summary -> VIEW CART BUTTON */}
        <div className="p-4 bg-white border-t">
          <div className="max-w-md mx-auto">
            <button
              onClick={() => setShowCartModal(true)}
              disabled={cart.length === 0}
              className="w-full flex items-center justify-between gap-3 py-3 px-4 bg-rose-500 text-white rounded-lg font-semibold shadow-md disabled:opacity-50"
              aria-label="View cart"
            >
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6h15l-1.5 9h-12z"></path><circle cx="9" cy="20" r="1"></circle><circle cx="19" cy="20" r="1"></circle></svg>
                <span>View Cart</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium">{totalItems} item{totalItems !== 1 ? "s" : ""}</div>
                <div className="text-sm">₹{grandTotal.toFixed(2)}</div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* CART MODAL */}
      {showCartModal && (
        <motion.div
          className="fixed inset-0 z-[10000] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowCartModal(false)} />
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 18, stiffness: 260 }}
            /* constrain width so modal never overflows mobile container */
            className="bg-white rounded-2xl shadow-xl z-[10001] w-full max-w-[360px] mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h4 className="font-bold">Cart ({totalItems} items)</h4>
              <button onClick={() => setShowCartModal(false)} className="p-2 text-gray-600"><FaTimes /></button>
            </div>

            <div className="p-4 space-y-3">
              {cart.length === 0 ? (
                <div className="text-center text-gray-500 py-6">Cart is empty</div>
              ) : (
                <>
                  <div className="space-y-2">
                    {cart.map((item) => (
                      <div key={item.id} className="flex items-center justify-between text-sm">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{item.name}</div>
                          <div className="text-gray-500">₹{item.price || 0} × {item.qty}</div>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <button onClick={() => decrementQty(item.id)} className="w-8 h-8 rounded bg-gray-100 text-gray-600 flex items-center justify-center">
                            <FaMinus size={12} />
                          </button>
                          <span className="font-medium w-6 text-center">{item.qty}</span>
                          <button onClick={() => incrementQty(item.id)} className="w-8 h-8 rounded bg-gray-100 text-gray-600 flex items-center justify-center">
                            <FaPlus size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2 text-sm border-t pt-3">
                    <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
                    {discount > 0 && <div className="flex justify-between text-rose-600"><span>Discount ({discountPercent}%)</span><span>-₹{discount.toFixed(2)}</span></div>}
                    <div className="flex justify-between"><span>Tax (5%)</span><span>₹{tax.toFixed(2)}</span></div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2"><span>Total</span><span>₹{grandTotal.toFixed(2)}</span></div>
                  </div>

                  <div className="pt-3">
                    <button
                      onClick={() => {
                        // close cart modal then place order
                        setShowCartModal(false);
                        handlePlaceOrder();
                      }}
                      disabled={cart.length === 0 || !restaurantId}
                      className="w-full py-3 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpdate ? "Update Order" : submitText}
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
}
